export const defaultHeadersWithoutSensitiveData = [
    'accept',
    'accept-encoding',
    'accept-language',
    'access-control-allow-headers',
    'access-control-allow-methods',
    'access-control-allow-origin',
    'cache-control',
    'connection',
    'content-encoding',
    'content-length',
    'content-type',
    'date',
    'etag',
    'expires',
    'host',
    'last-modified',
    'pragma',
    'server',
    'strict-transport-security',
    'timing-allow-origin',
    'transfer-encoding',
    'user-agent',
    'vary',
    'x-content-type-options',
    'x-frame-options',
    'x-xss-protection',
    'x-yandex-telemetry-trace-id',

    // Awacs balancers
    'x-yandex-req-id',

    // S3 CDN
    'x-nginx-request-id',

    // OTel трейсинг (W3C Trace Context)
    'traceparent',
] as const;

type HttpHeaders = Record<string, string | string[] | undefined>;

type Options = {
    headersWithoutSensitiveData?: Set<string>;
    cookiesWithoutSensitiveData?: Set<string>;
};

const defaultOptions = {
    headersWithoutSensitiveData: new Set(defaultHeadersWithoutSensitiveData),
    cookiesWithoutSensitiveData: new Set(),
} satisfies Options;

/**
 * Скрывает значения всех неизвестных заголовков и кук,
 * чтобы предотвратить утечку чувствительных данных в системы логирования.
 * @param headers Оригинальные хэдеры
 * @param options Список заголовков и кук, значения которых нужно оставить as-is.
 * @returns Хэдеры, у которых скрыты значения
 */
export function sanitizeHttpHeaders(headers: HttpHeaders, options: Options = {}): HttpHeaders {
    const {
        headersWithoutSensitiveData = defaultOptions.headersWithoutSensitiveData,
        cookiesWithoutSensitiveData = defaultOptions.cookiesWithoutSensitiveData,
    } = options;
    const res: HttpHeaders = {};

    for (const name of Object.keys(headers)) {
        let value = headers[name] ?? '';
        const normalizedName = name.toLowerCase();

        if (!headersWithoutSensitiveData.has(normalizedName)) {
            if (normalizedName === 'cookie') {
                value = map(value, v => sanitizeCookie(v, cookiesWithoutSensitiveData));
            } else {
                value = map(value, v => overrideValue(v));
            }
        }

        res[name] = value;
    }

    return res;
}

function map(value: string | string[], mapper: (value: string) => string) {
    if (Array.isArray(value)) {
        return value.map(v => mapper(v));
    }
    return mapper(value);
}

function overrideValue(value: string) {
    return `(redacted_${value.length}_chars)`;
}

function sanitizeCookie(value: string, cookiesWithoutSensitiveData: Set<string>) {
    const pairs = value.split(';');
    const res: string[] = [];
    for (const pair of pairs) {
        const index = pair.indexOf('=');
        if (index === -1) {
            res.push(pair);
            continue;
        }

        const name = pair.slice(0, index).trim();
        let value = tryDecode(pair.slice(index + 1).trim());

        if (!cookiesWithoutSensitiveData.has(name)) {
            value = overrideValue(value);
        }

        res.push(`${name}=${encodeURIComponent(value)}`);
    }
    return res.join(';');
}

function tryDecode(value: string) {
    try {
        return value.includes('%') ? decodeURIComponent(value) : value;
    } catch {
        return value;
    }
}
